import type { TaskType } from "../enums.js";
import type { BillingLine } from "./billing.js";
import type { TaskListUser } from "./task.js";

// ── Facture d'un relais ───────────────────────────────────────────────────

export const INVOICE_VERDICTS = ["valide", "a_verifier", "rejete"] as const;
export type InvoiceVerdict = (typeof INVOICE_VERDICTS)[number];

export const INVOICE_VERDICT_LABELS_FR: Record<InvoiceVerdict, string> = {
  valide: "Validable",
  a_verifier: "À vérifier",
  rejete: "Rejetée",
};

export const INVOICE_REVIEW_STATUSES = ["pending", "accepted", "rejected"] as const;
export type InvoiceReviewStatus = (typeof INVOICE_REVIEW_STATUSES)[number];

export const INVOICE_REVIEW_STATUS_LABELS_FR: Record<InvoiceReviewStatus, string> = {
  pending: "À traiter",
  accepted: "Bon à payer",
  rejected: "Refusée",
};

export const INVOICE_EXTRACTION_STATUSES = ["pending", "ok", "empty", "failed"] as const;
export type InvoiceExtractionStatus = (typeof INVOICE_EXTRACTION_STATUSES)[number];

export const INVOICE_ANALYSIS_STATUSES = ["pending", "running", "done", "failed"] as const;
export type InvoiceAnalysisStatus = (typeof INVOICE_ANALYSIS_STATUSES)[number];

export const INVOICE_SOURCES = ["relais", "admin"] as const;
export type InvoiceSource = (typeof INVOICE_SOURCES)[number];

// ── Résultat du contrôle automatique ──────────────────────────────────────
//
// Forme rendue par le modèle, en français. Tout est nullable : une facture
// scannée illisible produit un contrôle presque vide, qui doit rester
// affichable plutôt que de faire échouer la lecture.

export const KM_VERDICTS = ["ok", "suspect", "anomalie", "non_verifiable"] as const;
export type KmVerdict = (typeof KM_VERDICTS)[number];

export const MATCH_CONFIDENCES = ["certain", "probable", "douteux"] as const;
export type MatchConfidence = (typeof MATCH_CONFIDENCES)[number];

export const ANOMALY_SEVERITIES = ["bloquante", "majeure", "mineure"] as const;
export type AnomalySeverity = (typeof ANOMALY_SEVERITIES)[number];

export type InvoiceMatchedTask = {
  task_id: string | null;
  ref: string | null;
  client: string | null;
  type_mission: TaskType | null;
  date_intervention: string | null;
  montant_facture_ht: number | null;
  /** Prix convenu sur la mission, sinon grille tarifaire. Hors kilomètres. */
  montant_attendu_ht: number | null;
  ecart_pct: number | null;
  confiance: MatchConfidence;
  commentaire: string | null;
};

export type InvoiceKmLine = {
  libelle: string | null;
  km_factures: number | null;
  tarif_eur_km: number | null;
  montant_ht: number | null;
  /** Aller-retour retenu (itinéraire joint si fourni, sinon estimation). */
  aller_retour_km: number | null;
  km_attendus: number | null;
  montant_attendu_ht: number | null;
  source_distance: "itineraire_joint" | "estimation" | "inconnue";
};

export type InvoiceKmControl = {
  verdict: KmVerdict;
  lignes: InvoiceKmLine[];
  commentaire: string | null;
};

export type InvoiceFormChecks = {
  numero_present: boolean;
  date_presente: boolean;
  emetteur_present: boolean;
  montants_presents: boolean;
  /** Mention de franchise de TVA acceptée en lieu et place du détail TVA. */
  franchise_tva: boolean;
  destinataire_greenkub: boolean;
};

/**
 * Ligne facturée qui ne correspond à aucune mission et qui est légitime :
 * frais d'onboarding, frais de dossier. Greenkub les accepte ; le contrôle
 * vérifie leur cohérence, il ne les refuse pas.
 */
export type InvoiceOffMissionLine = {
  libelle: string | null;
  montant_ht: number | null;
  /** Le justificatif annoncé sur la facture est-il joint au dépôt ? */
  justificatif_joint: boolean;
};

export type InvoiceAnomaly = {
  gravite: AnomalySeverity;
  libelle: string;
};

export type InvoiceHeader = {
  numero: string | null;
  date: string | null;
  emetteur: string | null;
  montant_ht: number | null;
  montant_tva: number | null;
  montant_ttc: number | null;
};

export type InvoiceAnalysis = {
  decision: InvoiceVerdict;
  resume: string;
  relais_identifie: boolean;
  relais_nom: string | null;
  facture: InvoiceHeader;
  taches_rapprochees: InvoiceMatchedTask[];
  lignes_hors_mission: InvoiceOffMissionLine[];
  controle_km: InvoiceKmControl;
  controles_forme: InvoiceFormChecks;
  anomalies: InvoiceAnomaly[];
};

// ── Enregistrement stocké ─────────────────────────────────────────────────

export type RelaisInvoice = {
  id: string;
  relais_id: string | null;
  uploaded_by: string;
  source: InvoiceSource;
  file_name: string;
  storage_path: string;
  uploaded_at: string;
  extraction_status: InvoiceExtractionStatus;
  page_count: number | null;
  analysis_status: InvoiceAnalysisStatus;
  analysis: InvoiceAnalysis | null;
  analysis_model: string | null;
  analysis_error: string | null;
  analysed_at: string | null;
  verdict: InvoiceVerdict | null;
  invoice_number: string | null;
  invoice_date: string | null;
  amount_ht: number | null;
  amount_tva: number | null;
  amount_ttc: number | null;
  review_status: InvoiceReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Pièce justificative attachée à une facture (itinéraire, justificatif). Elle
 * n'a pas de verdict propre : elle entre dans le contrôle de sa facture.
 */
export type RelaisInvoiceAttachment = {
  id: string;
  invoice_id: string;
  file_name: string;
  storage_path: string;
  uploaded_at: string;
  extraction_status: InvoiceExtractionStatus;
  page_count: number | null;
  /** URL signée, courte durée. Null hors détail ou si la signature a échoué. */
  signed_url?: string | null;
};

export type RelaisInvoiceDetail = RelaisInvoice & {
  attachments: RelaisInvoiceAttachment[];
  relais: TaskListUser | null;
  uploaded_by_user: TaskListUser | null;
  reviewed_by_user: TaskListUser | null;
  /** URL signée du PDF, courte durée. Null si la signature a échoué. */
  signed_url: string | null;
  /** Texte extrait — admin uniquement, volumineux : absent des listes. */
  extracted_text?: string | null;
};

// ── Vue « contrôle de gestion » ───────────────────────────────────────────

export type ControlGestionRelaisRow = {
  relais_id: string;
  name: string;
  email: string | null;
  missions_count: number;
  /** Ce que Greenkub doit : somme des lignes de facturation de la période. */
  total_a_facturer: number;
  /** Ce que le relais a facturé : somme des montants HT de ses factures. */
  total_facture_ht: number;
  /** total_facture_ht − total_a_facturer. Positif = facturé en trop. */
  ecart: number;
  invoices_count: number;
  invoices_pending_review: number;
  /** Factures dont le contrôle automatique a levé au moins un doute. */
  invoices_flagged: number;
};

export type ControlGestionSummary = {
  /**
   * Référent dont la supervision rend une VT ou une réception de supports due.
   * Null = introuvable : les types qui l'exigent sont alors écartés, et
   * l'écran doit le dire plutôt que d'afficher un tableau vide sans raison.
   */
  referent: { id: string; name: string; email: string } | null;
  /** Bornes effectivement appliquées, en ISO. */
  from: string;
  to: string;
  lines: BillingLine[];
  per_relais: ControlGestionRelaisRow[];
  total_a_facturer: number;
  total_facture_ht: number;
  ecart: number;
  missions_count: number;
};

// ── Payloads ──────────────────────────────────────────────────────────────

export type InvoiceUploadUrlPayload = {
  /** Sert uniquement à choisir l'extension du chemin de stockage. */
  file_name: string;
  /** Admin déposant pour un relais. Ignoré (forcé à l'acteur) pour un relais. */
  relais_id?: string | null;
};

export type InvoiceUploadUrlResponse = {
  storage_path: string;
  token: string;
};

export type InvoiceCreatePayload = {
  storage_path: string;
  file_name: string;
  relais_id?: string | null;
};

export type InvoiceReviewPayload = {
  review_status: Exclude<InvoiceReviewStatus, "pending">;
  note?: string;
};
